import type { BreadcrumbSegment } from '@/components/PageBreadcrumbs';
import { listReturnHref } from '@/navigation/list-return';
import { getActiveNavItemId, navItemsForPath, type NavItem } from '@/navigation/nav-items';

/**
 * The sidebar section a path belongs to ("Fleet" for `/fleet/…`), from the nav
 * definitions. Null for Home and for pages outside the sidebar.
 */
export function navSectionFor(pathname: string): NavItem | null {
  const items = navItemsForPath(pathname);
  const activeId = getActiveNavItemId(pathname, items);
  if (!activeId) return null;
  const section =
    items.find((item) => item.id === activeId || item.children?.some((child) => child.id === activeId)) ??
    null;
  return section && section.path !== '/' ? section : null;
}

/**
 * Breadcrumbs for a page in a sidebar section, from the nav definitions: the section, then
 * the page when it isn't the section's first page, then `more` (usually the record).
 *
 * `sectionBreadcrumbItems('/fleet')` gives Fleet. A record page passes its
 * list's path and names the record with PageHeader's `recordTitle`, which adds the last crumb and
 * the tab title.
 *
 * Each crumb links to its page as this tab last showed it (`listReturnHref`), so going back to a
 * list keeps its filters, search and page.
 */
export function sectionBreadcrumbItems(pagePath: string, ...more: BreadcrumbSegment[]): BreadcrumbSegment[] {
  const activeId = getActiveNavItemId(pagePath, navItemsForPath(pagePath));
  const section = navSectionFor(pagePath);
  if (!section) return more;
  const items: BreadcrumbSegment[] = [{ label: section.label, href: listReturnHref(section.path) }];
  const child = section.children?.find((c) => c.id === activeId);
  if (child && child.path !== section.path) items.push({ label: child.label, href: listReturnHref(child.path) });
  return [...items, ...more];
}
